import { chunk } from 'lodash-es'
import proxyConfig from 'Config/proxy_config.json'

const crc32Table = (() => {
  const table: number[] = []
  for (let i = 0; i < 256; i++) {
    let crc = i
    for (let j = 0; j < 8; j++) {
      crc = (crc & 1) === 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1
    }
    table[i] = crc >>> 0
  }
  return table
})()

export const formatBytes = (bytes: number | unknown, decimals = 2) => {
  if (!(typeof bytes === 'number' && isFinite(bytes))) return 'Unknown'
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  let i = Math.floor(Math.log(bytes) / Math.log(k))
  if (i < 0) {
    i = 0
  }
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export const getPowNumber = (bytes: number) => {
  if (bytes === 0) return 0
  const k = 1024
  let i = Math.floor(Math.log(bytes) / Math.log(k))
  if (i < 0) {
    i = 0
  }
  return Math.min(i, 4)
}

export const formatBytesByPow = (bytes: number, pow: number, decimals = 2) => {
  const dm = decimals < 0 ? 0 : decimals
  const k = 1024
  return parseFloat((bytes / Math.pow(k, pow)).toFixed(dm))
}

export const numberFormatter = (num: number | unknown, digits?: number) => {
  if (!(typeof num === 'number' && isFinite(num))) return 'NaN'
  const si = [
    { value: 1e18, symbol: 'E' },
    { value: 1e15, symbol: 'P' },
    { value: 1e12, symbol: 'T' },
    { value: 1e9, symbol: 'G' },
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'K' }
  ]
  for (let i = 0; i < si.length; i++) {
    if (num >= si[i].value) {
      return (
        (num / si[i].value + 0.1).toFixed(digits).replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1') +
        ' ' +
        si[i].symbol
      )
    }
  }
  return num.toFixed(digits).toString()
}

export const downloadCSV = (data: string[][], filename: string) => {
  const csvContent = 'data:text/csv;charset=utf-8,' + data.map(e => e.join(',')).join('\n')
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', filename + '.csv')
  document.body.appendChild(link)
  link.click()
}

export async function chunckFn(
  func: (item: any) => void | Promise<void>,
  list: any[],
  chunckSize = 5
) {
  const chuncks = chunk(list, chunckSize)
  for (const ck of chuncks) {
    await Promise.allSettled(
      ck.map(c => {
        return func(c)
      })
    )
  }
}

export const getEnv = () => {
  if (proxyConfig.proxyLocation.includes('test')) {
    return 'test'
  }
  if (proxyConfig.proxyLocation.includes('stg')) {
    return 'staging'
  }
  return 'production'
}

export const removeTrailingSlash = (url: string) => {
  if (url.endsWith('/')) {
    return url.slice(0, -1)
  }
  return url
}

export const hash = (str: string): string => {
  const bytes = new TextEncoder().encode(str)
  let crc = 0xffffffff

  for (const byte of bytes) {
    crc = crc32Table[(crc ^ byte) & 0xff] ^ (crc >>> 8)
  }

  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0')
}

export const randomString = (length: number): string => {
  return Math.random()
    .toString(36)
    .substring(2, 2 + length)
}

export const extractRegionFromS3TableUri = (uri: string): string => {
  try {
    // URI format: https://s3tables.ap-northeast-1.amazonaws.com/iceberg
    const url = new URL(uri)
    const hostname = url.hostname
    // Extract region from hostname: s3tables.ap-northeast-1.amazonaws.com
    const parts = hostname.split('.')
    if (parts.length >= 2 && parts[0] === 's3tables') {
      return parts[1] // ap-northeast-1
    }
  } catch (error) {
    console.warn('Failed to parse S3 Table URI:', uri, error)
  }
  return ''
}

// Returns the proxy endpoint to use for API calls. When the
// `is_use_proxy_v2` flag is set to `true` in localStorage, swaps the
// `proxy.` host prefix for `proxy-v2.` so engineers can route to the
// v2 proxy at runtime. Otherwise returns the value baked into
// proxy_config.json.
export const getProxyLocation = (): string => {
  const value = localStorage.getItem('is_use_proxy_v2')
  if (value === 'true') {
    return proxyConfig.proxyLocation.replace('proxy.', 'proxy-v2.')
  }
  return proxyConfig.proxyLocation
}

export const commonUtils = () => {
  return {
    formatBytes,
    getPowNumber,
    formatBytesByPow,
    numberFormatter,
    downloadCSV,
    chunckFn,
    getEnv,
    removeTrailingSlash,
    hash,
    randomString,
    extractRegionFromS3TableUri
  }
}
